<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import type { User } from '@/types/auth'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const emit = defineEmits<{
  'login-success': [user: User]
}>()

const { t } = useI18n()
const { login } = useAuth()

const email = ref('')
const password = ref('')
const remember = ref(false)
const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

async function handleSubmit(): Promise<void> {
  errors.value = {}
  generalError.value = ''

  if (!email.value) {
    errors.value = { ...errors.value, email: [t('common.required_field')] }
  }
  if (!password.value) {
    errors.value = { ...errors.value, password: [t('common.required_field')] }
  }
  if (Object.keys(errors.value).length > 0) return

  loading.value = true
  try {
    const user = await login({ email: email.value, password: password.value, remember: remember.value })
    emit('login-success', user)
  } catch (e: unknown) {
    console.error('Login error:', e)
    
    if (isAxiosError(e)) {
      const status = e.response.status
      const backendMessage = e.response.data.message
      
      if (status === 422 && e.response.data.errors) {
        // Validation errors - show field-specific errors
        errors.value = e.response.data.errors
        generalError.value = backendMessage || t('auth.login.error_validation')
      } else if (status === 401) {
        // Authentication failed - invalid credentials
        generalError.value = backendMessage || t('auth.login.error_invalid_credentials')
      } else if (status === 419) {
        // CSRF token mismatch - session expired
        generalError.value = t('auth.login.error_session_expired')
      } else if (status === 429) {
        // Too many attempts - rate limited
        generalError.value = t('auth.login.error_too_many_attempts')
      } else if (status >= 500) {
        // Server error
        generalError.value = t('auth.login.error_server')
      } else {
        // Other errors - try to show backend message if available
        generalError.value = backendMessage || t('common.error_generic')
      }
    } else {
      // Network error or CORS error
      // Check browser console for more details
      const errorMessage = e instanceof Error ? e.message : ''
      if (errorMessage.toLowerCase().includes('cors')) {
        generalError.value = t('auth.login.error_cors')
      } else {
        generalError.value = t('auth.login.error_network')
      }
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div>
    <h2 class="text-2xl font-bold text-white mb-1">{{ t('auth.login.heading') }}</h2>
    <p class="text-slate-400 text-sm mb-6">{{ t('auth.login.subheading') }}</p>

    <form novalidate @submit.prevent="handleSubmit" class="space-y-5">
      <div v-if="generalError" class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
        {{ generalError }}
      </div>

      <div>
        <label for="login-email" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('auth.login.email_label') }}
        </label>
        <input
          id="login-email"
          v-model="email"
          type="email"
          autocomplete="email"
          :placeholder="t('auth.login.email_placeholder')"
          class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          :class="errors.email ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        />
        <p v-if="errors.email" class="mt-1 text-xs text-red-400">{{ errors.email[0] }}</p>
      </div>

      <div>
        <label for="login-password" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('auth.login.password_label') }}
        </label>
        <input
          id="login-password"
          v-model="password"
          type="password"
          autocomplete="current-password"
          :placeholder="t('auth.login.password_placeholder')"
          class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          :class="errors.password ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        />
        <p v-if="errors.password" class="mt-1 text-xs text-red-400">{{ errors.password[0] }}</p>
      </div>

      <div class="flex items-center gap-2">
        <input
          id="login-remember"
          v-model="remember"
          type="checkbox"
          class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
        />
        <label for="login-remember" class="text-sm text-slate-300 cursor-pointer">
          {{ t('auth.login.remember_label') }}
        </label>
      </div>

      <button
        type="submit"
        :disabled="loading"
        class="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
      >
        <AppSpinner v-if="loading" size="button" />
        {{ loading ? t('common.loading') : t('auth.login.submit') }}
      </button>
    </form>

    <p class="mt-6 text-sm text-slate-400 text-center">
      {{ t('auth.login.no_account') }}
      <RouterLink to="/organizer/register" class="text-emerald-400 hover:text-emerald-300 font-medium ml-1">
        {{ t('auth.login.start_register') }}
      </RouterLink>
    </p>

    <p class="mt-3 text-sm text-center">
      <RouterLink
        :to="{ name: 'organizer.forgot-password', query: { context: 'organizer' } }"
        class="text-slate-400 hover:text-slate-300 text-xs"
      >
        {{ t('auth.forgot_password.heading') }}
      </RouterLink>
    </p>
  </div>
</template>
